import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Briefcase, MapPin, Scales, ArrowRight, Suitcase, Users, CheckCircle,
  User, Lock, ShieldCheck, Star, Globe, Flag,
  FacebookLogo, InstagramLogo, TiktokLogo, LinkedinLogo, CaretDown, GlobeHemisphereWest,
  Sparkle, AirplaneTilt, CalendarBlank
} from '@phosphor-icons/react';
import DropioLogo from '@/components/DropioLogo';
import LocationPicker from '@/components/LocationPicker';
import { API } from '@/App';

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('send');
  const [from, setFrom] = useState({ country: '', city: '' });
  const [to, setTo] = useState({ country: '', city: '' });
  const [date, setDate] = useState('');
  const [weight, setWeight] = useState('');
  const [stats, setStats] = useState(null);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Push the search criteria (incl. date) into the find-travelers route so it flows through
  // route matching / pricing / recommendations downstream.
  const handleAuth = () => {
    const params = new URLSearchParams();
    if (from.country) params.set('origin_country', from.country);
    if (from.city) params.set('origin_city', from.city);
    if (to.country) params.set('destination_country', to.country);
    if (to.city) params.set('destination_city', to.city);
    if (date) params.set('date', date);
    if (weight) params.set('weight', weight);
    params.set('intent', activeTab); // 'send' | 'travel'
    const qs = params.toString();
    navigate(`/signup${qs ? `?${qs}` : ''}`);
  };

  // Fetch real marketplace stats. Hybrid threshold: if <10 travelers AND <10 parcels,
  // we render honest beta messaging instead of fabricated numbers.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/public/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled && data) setStats(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const isLive = !!stats?.live;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <DropioLogo size={36} data-testid="nav-logo" />

            <div className="hidden lg:flex items-center gap-8">
              <a href="#how-it-works" className="text-sm font-semibold text-gray-700 hover:text-[#6D28D9] transition-colors" data-testid="nav-how">How It Works</a>
              <a href="#find-travelers" className="text-sm font-semibold text-gray-700 hover:text-[#6D28D9] transition-colors" data-testid="nav-find">Find Travelers</a>
              <button onClick={() => navigate('/become-a-traveler')} className="text-sm font-semibold text-gray-700 hover:text-[#6D28D9] transition-colors" data-testid="nav-become">Become a Traveler</button>
              <a href="#trust" className="text-sm font-semibold text-gray-700 hover:text-[#6D28D9] transition-colors" data-testid="nav-trust">Trust & Safety</a>
              <a href="#support" className="text-sm font-semibold text-gray-700 hover:text-[#6D28D9] transition-colors" data-testid="nav-support">Support</a>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/login')}
                className="px-5 py-2.5 text-sm font-semibold text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="login-button"
              >
                Log In
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="px-5 py-2.5 text-sm font-semibold text-white dropio-gradient-cta rounded-lg shadow-lg shadow-purple-200 hover:shadow-xl transition-all hover:-translate-y-0.5"
                data-testid="signup-button"
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Global Network Map */}
      <section className="relative overflow-hidden bg-white pt-0 pb-20" style={{ minHeight: '780px' }}>
        {/* Official client-provided Hero Background (4300×2400) — exact image, full visibility, no overlay/blur/opacity/filter */}
        <div className="absolute inset-0 z-0" data-testid="hero-background">
          <img
            src="https://customer-assets.emergentagent.com/job_traveler-marketplace/artifacts/ohm5x1bu_4300%20X%202400%20Hero%20Background.png"
            alt="Dropio global delivery network — verified travelers across continents"
            className="w-full h-full hero-bg-image"
            style={{ objectFit: 'cover', objectPosition: 'center center', opacity: 1, filter: 'none' }}
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-32">
          {/* Hero tagline above search card */}
          <p
            className="text-center text-sm md:text-base font-semibold tracking-wide text-gray-800 mb-6 px-4 py-1.5 bg-white/85 backdrop-blur rounded-full mx-auto shadow-sm"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', display: 'block', width: 'fit-content' }}
            data-testid="hero-tagline"
          >
            Connect with verified travelers across global delivery routes.
          </p>
          {/* Search Card */}
          <div className="bg-white rounded-2xl shadow-2xl shadow-purple-100/50 p-8 md:p-10 max-w-4xl mx-auto" data-testid="hero-search-card">
            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-8">
              <button
                onClick={() => setActiveTab('send')}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-semibold transition-all relative ${
                  activeTab === 'send' ? 'text-[#6D28D9]' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="tab-send-parcel"
              >
                <Package size={24} weight="duotone" />
                <span>Send a Parcel</span>
                {activeTab === 'send' && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#6D28D9] to-[#7C3AED] rounded-full"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab('travel')}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-semibold transition-all relative ${
                  activeTab === 'travel' ? 'text-[#6D28D9]' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="tab-travel-earn"
              >
                <Briefcase size={24} weight="duotone" />
                <span>Travel & Earn</span>
                {activeTab === 'travel' && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#6D28D9] to-[#7C3AED] rounded-full"></div>
                )}
              </button>
            </div>

            {/* Form — Beta: searchable AU/BD city dropdowns + date + weight.
                Layout: 4 cols on desktop, 2x2 grid on mobile (no horizontal scroll). */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">From</label>
                <LocationPicker value={from} onChange={setFrom} mode="flat" compact testIdPrefix="search-from" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">To</label>
                <LocationPicker value={to} onChange={setTo} mode="flat" compact testIdPrefix="search-to" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">
                  {activeTab === 'send' ? 'Date Needed' : 'Departure Date'}
                </label>
                <div className="relative">
                  <CalendarBlank size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    type="date"
                    value={date}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#6D28D9] focus:ring-2 focus:ring-purple-100 bg-white"
                    data-testid="search-date"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">
                  {activeTab === 'send' ? 'Weight' : 'Available Weight'}
                </label>
                <div className="relative">
                  <Scales size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="w-full pl-10 pr-8 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#6D28D9] focus:ring-2 focus:ring-purple-100 appearance-none bg-white"
                    data-testid="search-weight"
                  >
                    <option value="">Select</option>
                    <option value="0-5">0-5 kg</option>
                    <option value="5-10">5-10 kg</option>
                    <option value="10-20">10-20 kg</option>
                    <option value="20+">20+ kg</option>
                  </select>
                  <CaretDown size={14} weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            <button
              onClick={handleAuth}
              className="w-full dropio-gradient-cta text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 shadow-lg shadow-purple-200 hover:shadow-xl transition-all hover:-translate-y-0.5"
              data-testid="find-travelers-button"
            >
              <span>{activeTab === 'send' ? 'Find Matching Travelers' : 'Find Parcel Requests'}</span>
              <ArrowRight size={20} weight="bold" />
            </button>
          </div>

          {/* Stats line — hybrid: honest beta tags below threshold, real metrics once live */}
          {isLive ? (
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 text-sm text-gray-700" data-testid="hero-stats-live">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75 animate-ping"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span><strong className="font-bold">{stats.active_travelers.toLocaleString()}</strong> active travelers</span>
              </div>
              <span className="text-gray-300">•</span>
              <span><strong className="font-bold">{stats.active_parcels.toLocaleString()}</strong> live parcel requests</span>
              <span className="text-gray-300">•</span>
              <span><strong className="font-bold">{stats.countries_connected}</strong> countries connected</span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-2 mt-8 text-xs sm:text-sm" data-testid="hero-stats-beta">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur border border-purple-200 text-[#6D28D9] font-semibold shadow-sm">
                <Sparkle size={14} weight="fill" /> Founding Members Onboarding Now
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur border border-orange-200 text-[#C2410C] font-semibold shadow-sm">
                <Star size={14} weight="fill" /> Be Among the First 100 Verified Travelers
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur border border-pink-200 text-[#BE185D] font-semibold shadow-sm">
                <AirplaneTilt size={14} weight="fill" /> Australia ↔ Bangladesh Beta Launch
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Live Traveler Routes — only when threshold met. No fake names. */}
      {isLive && stats?.active_travelers > 0 && (
        <section className="bg-white px-4 sm:px-6 lg:px-8 pb-6" data-testid="live-routes-section">
          <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-md border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75 animate-ping"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
              </span>
              <span className="text-sm font-bold text-gray-900 tracking-wider">LIVE TRAVELER ROUTES</span>
              <span className="ml-auto text-sm font-semibold text-[#6D28D9]">{stats.active_travelers} active</span>
            </div>
            <button
              onClick={() => navigate('/signup')}
              className="w-full text-sm font-semibold text-[#6D28D9] hover:text-[#5B21B6] py-2"
              data-testid="cta-view-live-routes"
            >
              Sign up to view all {stats.active_travelers} active travelers →
            </button>
          </div>
        </section>
      )}

      {/* Live Parcel Requests — only when threshold met. No fake requests. */}
      {isLive && stats?.active_parcels > 0 && (
        <section className="bg-white px-4 sm:px-6 lg:px-8 pb-16" data-testid="live-parcels-section">
          <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-md border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
              </span>
              <span className="text-sm font-bold text-gray-900 tracking-wider">LIVE PARCEL REQUESTS</span>
              <span className="ml-auto text-sm font-semibold text-[#6D28D9]">{stats.active_parcels} live</span>
            </div>
            <button
              onClick={() => navigate('/signup')}
              className="w-full text-sm font-semibold text-[#6D28D9] hover:text-[#5B21B6] py-2"
              data-testid="cta-view-live-parcels"
            >
              Sign up to view all {stats.active_parcels} live parcel requests →
            </button>
          </div>
        </section>
      )}

      {/* Beta Trust Pillars — shown when threshold NOT met (replaces fake live strips) */}
      {!isLive && (
        <section className="bg-white px-4 sm:px-6 lg:px-8 pb-16" data-testid="beta-pillars-section">
          <div className="max-w-7xl mx-auto bg-gradient-to-br from-purple-50 via-white to-orange-50 rounded-2xl border border-purple-100 p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: Lock, title: 'Secure Escrow Payments', desc: 'Funds held until verified delivery. Auto-refunded on cancellation.', color: '#6D28D9', bg: '#F3F0FF' },
                { icon: ShieldCheck, title: 'AI Compliance Protection', desc: 'Every parcel scanned by Gemini Vision + Claude before pickup. Customs-safe by design.', color: '#BE185D', bg: '#FCE7F3' },
                { icon: Users, title: 'Verified Community Marketplace', desc: 'ID-verified travelers only. Founding members onboarded one by one.', color: '#C2410C', bg: '#FFEDD5' },
              ].map((p, idx) => {
                const Icon = p.icon;
                return (
                  <div key={idx} className="flex items-start gap-4" data-testid={`beta-pillar-${idx}`}>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: p.bg }}>
                      <Icon size={26} weight="duotone" style={{ color: p.color }} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900 mb-1">{p.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{p.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* How Dropio Works */}
      <section id="how-it-works" className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-14" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            How Dropio Works
          </h2>
          <div className="grid md:grid-cols-3 gap-6 items-start relative">
            {[
              { num: '1', icon: Suitcase, title: 'List or Post', desc: 'Post a parcel or list your journey in minutes.' },
              { num: '2', icon: Users, title: 'Match & Connect', desc: 'We connect you with verified travelers or senders.' },
              { num: '3', icon: Package, title: 'Deliver & Earn', desc: 'Items are delivered safely and both sides rate each other.' }
            ].map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={step.num} className="relative flex flex-col items-center text-center" data-testid={`how-step-${step.num}`}>
                  <div className="relative mb-5">
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-[#6D28D9] text-white text-xs font-bold flex items-center justify-center z-10">
                      {step.num}
                    </div>
                    <div className="w-24 h-24 rounded-full bg-purple-50 flex items-center justify-center">
                      <Icon size={44} weight="duotone" className="text-[#6D28D9]" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{step.title}</h3>
                  <p className="text-sm text-gray-600 max-w-[240px]">{step.desc}</p>
                  {idx < 2 && (
                    <div className="hidden md:block absolute top-12 left-[calc(50%+80px)] w-[calc(100%-160px)] border-t-2 border-dashed border-[#6D28D9]/30">
                      <ArrowRight size={16} weight="bold" className="absolute -right-2 -top-2 text-[#6D28D9]/40" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Two CTA Cards */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          {/* Send a Parcel Card */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#F3F0FF] to-[#EDE9FE] p-8 md:p-10" data-testid="cta-send-parcel">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Need to <br/>
                  <span className="dropio-gradient-text">Send a Parcel?</span>
                </h3>
                <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                  Save up to 50% compared to traditional couriers. Fast, reliable and trusted by thousands.
                </p>
                <ul className="space-y-2 mb-6">
                  {['Lower cost delivery', 'Door-to-door options', 'Global coverage'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle size={18} weight="fill" className="text-[#6D28D9] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={handleAuth}
                  className="dropio-gradient-cta text-white font-semibold px-6 py-3 rounded-lg flex items-center gap-2 hover:shadow-lg transition-all"
                  data-testid="send-parcel-cta"
                >
                  Send a Parcel
                  <ArrowRight size={18} weight="bold" />
                </button>
              </div>
              <div className="hidden md:block">
                <img
                  src="https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?auto=format&fit=crop&w=400&q=80"
                  alt="Parcel handover"
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* Traveling Soon Card */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#FFF1E6] to-[#FED7AA] p-8 md:p-10" data-testid="cta-travel">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Traveling <br/>
                  <span className="bg-gradient-to-r from-[#F97316] to-[#EF4444] bg-clip-text text-transparent">Soon?</span>
                </h3>
                <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                  Turn your free luggage space into extra cash by helping people deliver their items.
                </p>
                <ul className="space-y-2 mb-6">
                  {['Earn extra income', 'Flexible & on your terms', 'Safe & secure'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle size={18} weight="fill" className="text-[#F97316] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={handleAuth}
                  className="bg-gradient-to-r from-[#F97316] to-[#EF4444] text-white font-semibold px-6 py-3 rounded-lg flex items-center gap-2 hover:shadow-lg transition-all"
                  data-testid="list-journey-cta"
                >
                  List My Journey
                  <ArrowRight size={18} weight="bold" />
                </button>
              </div>
              <div className="hidden md:block">
                <img
                  src="https://images.pexels.com/photos/6726195/pexels-photo-6726195.jpeg?auto=compress&cs=tinysrgb&w=400"
                  alt="Traveler at airport"
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Features Row */}
      <section id="trust" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: User, title: 'Verified Users', desc: 'ID verified for all travelers and senders' },
              { icon: Lock, title: 'Secure Payments', desc: 'Escrow payments keep your money safe' },
              { icon: ShieldCheck, title: 'Delivery Protection', desc: 'Up to $500 insurance on eligible deliveries' },
              { icon: Star, title: 'Community Ratings', desc: 'Real reviews from verified users' }
            ].map((f, idx) => {
              const Icon = f.icon;
              return (
                <div key={idx} className="flex items-start gap-3" data-testid={`trust-feature-${idx}`}>
                  <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                    <Icon size={24} weight="duotone" className="text-[#6D28D9]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">{f.title}</h4>
                    <p className="text-sm text-gray-600 leading-snug">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Bar — hybrid: real DB metrics when live, honest beta tiles below threshold */}
      <section className="bg-[#0B1733] py-10" data-testid="stats-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {(isLive
            ? [
                { icon: Users, num: stats.active_travelers.toLocaleString(), label: 'Active Travelers' },
                { icon: Globe, num: stats.active_parcels.toLocaleString(), label: 'Live Parcel Requests' },
                { icon: Flag, num: String(stats.countries_connected), label: 'Countries Connected' },
                { icon: CheckCircle, num: stats.successful_deliveries.toLocaleString(), label: 'Successful Deliveries' },
              ]
            : [
                { icon: Sparkle, num: 'Beta', label: 'Founding Members Onboarding Now' },
                { icon: Lock, num: '100%', label: 'Secure Escrow Payments' },
                { icon: ShieldCheck, num: 'AI', label: 'Compliance Protection on Every Parcel' },
                { icon: Users, num: 'AU ↔ BD', label: 'Beta Launch Corridor' },
              ]
          ).map((s, idx) => {
            const Icon = s.icon;
            return (
              <div key={idx} className="flex items-center gap-4" data-testid={`stat-${idx}`}>
                <Icon size={40} weight="duotone" className="text-[#A78BFA]" />
                <div>
                  <p className="text-2xl md:text-3xl font-extrabold text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{s.num}</p>
                  <p className="text-xs text-gray-300">{s.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Gradient CTA Banner */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl dropio-gradient-banner p-10 md:p-14">
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/5"></div>
            <div className="absolute right-20 bottom-10 grid grid-cols-6 gap-2 opacity-20">
              {[...Array(36)].map((_, i) => (
                <div key={i} className="w-1.5 h-1.5 rounded-full bg-white"></div>
              ))}
            </div>
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Join the Future of <br/>
                  Crowd-Powered Logistics
                </h3>
                <p className="text-white/90 text-sm md:text-base">
                  Whether you&apos;re sending or traveling,<br/>
                  Dropio helps you unlock the value of every journey.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={handleAuth}
                  className="bg-white text-gray-900 font-semibold px-6 py-4 rounded-xl flex items-center justify-between gap-3 hover:shadow-xl transition-all min-w-[200px]"
                  data-testid="banner-become-traveler"
                >
                  <span>Become a Traveler</span>
                  <ArrowRight size={18} weight="bold" />
                </button>
                <button
                  onClick={handleAuth}
                  className="bg-white text-gray-900 font-semibold px-6 py-4 rounded-xl flex items-center justify-between gap-3 hover:shadow-xl transition-all min-w-[200px]"
                  data-testid="banner-send-parcel"
                >
                  <span>Send a Parcel</span>
                  <ArrowRight size={18} weight="bold" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <DropioLogo size={32} />
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm font-medium text-gray-700">
            <a href="#" className="hover:text-[#6D28D9] transition-colors">About Us</a>
            <a href="#how-it-works" className="hover:text-[#6D28D9] transition-colors">How It Works</a>
            <a href="#trust" className="hover:text-[#6D28D9] transition-colors">Trust & Safety</a>
            <button onClick={() => navigate('/restricted-goods')} className="hover:text-[#6D28D9] transition-colors text-left" data-testid="footer-restricted">Restricted Items</button>
            <a href="#" className="hover:text-[#6D28D9] transition-colors">FAQs</a>
            <a href="#" className="hover:text-[#6D28D9] transition-colors">Contact</a>
          </div>
          <div className="flex items-center gap-4">
            <button className="flex items-center gap-1 text-sm text-gray-700">
              <GlobeHemisphereWest size={18} weight="bold" />
              <span>English</span>
              <CaretDown size={12} weight="bold" />
            </button>
            <div className="flex items-center gap-3 text-gray-600">
              <a href="#" className="hover:text-[#6D28D9]"><FacebookLogo size={20} weight="fill" /></a>
              <a href="#" className="hover:text-[#6D28D9]"><InstagramLogo size={20} weight="fill" /></a>
              <a href="#" className="hover:text-[#6D28D9]"><TiktokLogo size={20} weight="fill" /></a>
              <a href="#" className="hover:text-[#6D28D9]"><LinkedinLogo size={20} weight="fill" /></a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
